import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Link, Popover, Typography } from '@heroui/react';
import {
    countParam,
    renderNotice,
    type CatalogImportNoticeTone,
} from '@/pages/catalogImport/notices';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportRowNote } from '../../importRows';
import { RowExclusionLockContext } from '../rowExclusion';

const NOTE_ICON: Record<CatalogImportNoticeTone, string> = {
    danger: 'gravity-ui:circle-exclamation',
    warning: 'gravity-ui:triangle-exclamation',
};

const NOTE_TONE_LABEL_KEY: Record<CatalogImportNoticeTone, string> = {
    danger: 'catalogImport.products.note.danger',
    warning: 'catalogImport.products.note.warning',
};

type NoteCellProps = {
    note: ImportRowNote;
    rowNumber: number;
    /** The row is excluded: the note keeps its sentence and icon, in the muted colour. */
    isMuted?: boolean;
    /**
     * The sentence wraps instead of truncating: a phone card, where no
     * pointer can hover the title for the full text.
     */
    wraps?: boolean;
    /**
     * The row is flagged (`isFlaggedRow`): the note ends with a Fix link
     * that opens the row in the drawer. Left out, the note has none.
     */
    onFix?: (rowNumber: number) => void;
};

/**
 * A row's note: one sentence with a small icon. A problem reads in danger
 * tone, a warning in the body colour behind a warning icon; the tone is also
 * named for screen readers, so it is never colour alone. In the table the
 * sentence truncates to its column and is itself the trigger of a HeroUI
 * `Popover` that shows the whole note, wrapped (bos-app#222): pressed with
 * the pointer, or Enter or Space once Tab reaches it, and "Read the whole
 * note" as its hover hint. Its press stays its own, so the row does not open
 * too. On a phone card the sentence wraps instead (`wraps`) and needs none.
 * On an excluded row the note is muted with the rest of the row: it still
 * says why the row needed attention, without asking for it.
 *
 * A flagged row's note ends with **Fix** (`onFix`), a HeroUI `Link` named
 * with its row for screen readers ("Fix row 902") that opens the row in the
 * drawer; its press stays its own, so the row's press does not fire too.
 * While the import runs (`RowExclusionLockContext`) it waits. A barcode
 * case (`note.choice`, bos-app#232) is the owner's choice per product: its
 * link reads **Choose** ("Choose for row 866"), and an IMEI's note says
 * how many rows of the product hold IMEIs.
 */
export const NoteCell: React.FC<NoteCellProps> = ({
    note,
    rowNumber,
    isMuted = false,
    wraps = false,
    onFix,
}) => {
    const { t } = useTranslation('pages');
    const isLocked = React.useContext(RowExclusionLockContext);
    const imeiRows = note.choice?.imeiRows ?? null;
    const sentence =
        imeiRows === null
            ? renderNotice(t, note.notice)
            : t('catalogImport.products.note.imeiRows', {
                  count: imeiRows,
                  rows: countParam(imeiRows),
              });
    const isDanger = !isMuted && note.tone === 'danger';
    const toneClass = isMuted ? 'text-muted' : isDanger && 'text-danger';
    /** The tone's icon, nudged down to the first line where the text wraps. */
    const iconOf = (isWrapped: boolean) => (
        <Icon
            aria-hidden
            className={cn(
                'size-3.5 shrink-0',
                isWrapped && 'mt-[3px]',
                !isMuted && note.tone === 'warning' && 'text-warning',
            )}
            icon={NOTE_ICON[note.tone]}
        />
    );

    return (
        <span
            className={cn(
                'flex min-w-0 gap-1.5 text-sm',
                wraps ? 'items-start' : 'items-center',
                toneClass,
            )}
            data-muted={isMuted || undefined}
            data-tone={note.tone}
            {...getNodeTestIdProp(`catalog-import-row-note-${rowNumber}`)}
        >
            {iconOf(wraps)}
            <span className="sr-only">{t(NOTE_TONE_LABEL_KEY[note.tone])}</span>
            {wraps ? (
                <span className="min-w-0 break-words">{sentence}</span>
            ) : (
                <Popover>
                    <Popover.Trigger
                        className="min-w-0 truncate"
                        tabIndex={0}
                        title={t('catalogImport.products.note.showFull')}
                        {...getNodeTestIdProp(
                            `catalog-import-row-note-trigger-${rowNumber}`,
                        )}
                    >
                        {sentence}
                    </Popover.Trigger>
                    <Popover.Content
                        className="max-w-80"
                        placement="bottom start"
                    >
                        <Popover.Dialog
                            aria-label={t(
                                'catalogImport.products.columns.note',
                            )}
                            {...getNodeTestIdProp(
                                `catalog-import-row-note-full-${rowNumber}`,
                            )}
                        >
                            {/* Typography has no danger colour: a
                                problem's tone stays a class. */}
                            <Typography
                                className={cn(
                                    'flex items-start gap-1.5',
                                    isDanger && 'text-danger',
                                )}
                                color={isMuted ? 'muted' : 'default'}
                                type="body-sm"
                            >
                                {iconOf(true)}
                                <span className="sr-only">
                                    {t(NOTE_TONE_LABEL_KEY[note.tone])}
                                </span>
                                <span className="min-w-0 break-words">
                                    {sentence}
                                </span>
                            </Typography>
                        </Popover.Dialog>
                    </Popover.Content>
                </Popover>
            )}
            {onFix === undefined ? null : (
                <Link
                    aria-label={t(
                        note.choice
                            ? 'catalogImport.products.note.chooseRow'
                            : 'catalogImport.products.note.fixRow',
                        { row: rowNumber },
                    )}
                    className="shrink-0"
                    isDisabled={isLocked}
                    onPress={() => onFix(rowNumber)}
                    {...getNodeTestIdProp(
                        `catalog-import-row-fix-${rowNumber}`,
                    )}
                >
                    {t(
                        note.choice
                            ? 'catalogImport.products.note.choose'
                            : 'catalogImport.products.note.fix',
                    )}
                </Link>
            )}
        </span>
    );
};
